import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  HiChevronDown,
  HiChevronRight,
  HiDotsVertical,
  HiOutlineLibrary,
  HiOutlineTrash,
} from "react-icons/hi";
import { BsFillExclamationCircleFill } from "react-icons/bs";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import ConfirmDialog from "../common/ConfirmDialog";
import deleteAccountIcon from "../../assets/images/bankStatements/delete-account.svg";
import { getBankIcon } from "../../constants/bankIcons";
import {
  BankStatementUploadItem,
  PulseBankAccount,
  PulseRangeUploadInput,
  UploadQueueStatus,
} from "./types";
import {
  DIALOG_MODAL_STYLES,
  hasProcessingDocuments,
  isActivePipelineStatus,
  isDocumentPasswordRequired,
  isRangeUploadPending,
  isSameRangeUpload,
  maskAccountNumber,
  pipelineStatusFromAccount,
  truncateBankName,
} from "./utils";
import { DocumentList } from "./DocumentList";
import { FileUploadQueue, FileUploadQueueHandle } from "./FileUploadQueue";
import { AccountStatus } from "./AccountStatusRow";
import { MissingPeriodPanel } from "./MissingPeriodPanel";
import { usePersistedExpansion } from "./usePersistedExpansion";
import { useBankStatementsActions } from "./BankStatementsContext";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import styles from "./BankStatements.module.scss";

const ACTIVE_UPLOAD_STATUSES = new Set<UploadQueueStatus>([
  "queued",
  "checking",
  "uploading",
  "password_verified",
  "uploaded",
]);

export const BankAccountCard = ({
  account,
  companyId,
}: {
  account: PulseBankAccount;
  companyId: number;
}) => {
  const bankName = account.bankName || "Bank account";
  const isEmptyAccount = account.documents.length === 0;
  const bankIconUrl = getBankIcon(bankName);
  const { expanded, toggleExpanded } = usePersistedExpansion(
    companyId,
    account.id
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [uploadItems, setUploadItems] = useState<BankStatementUploadItem[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [failedBankIconUrl, setFailedBankIconUrl] = useState<string | null>(
    null
  );
  const uploadQueueRef = useRef<FileUploadQueueHandle | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const {
    submitAnalysis,
    removeDocument,
    submitDocumentPassword,
    deleteAccount,
  } = useBankStatementsActions();
  const handleReady = useCallback(
    (docIds: number[]) => submitAnalysis(docIds),
    [submitAnalysis]
  );
  const stagedQueueDocumentIds = new Set(
    uploadItems.flatMap((item) => (item.doc?.id ? [item.doc.id] : []))
  );
  const visibleDocuments = account.documents.filter(
    (doc) => !stagedQueueDocumentIds.has(doc.id)
  );
  const accountPipelineStatus = pipelineStatusFromAccount(account);
  const requiresPassword = account.documents.some(isDocumentPasswordRequired);
  const hasBackendProcessing =
    !isEmptyAccount &&
    (hasProcessingDocuments(account.documents) ||
      (isActivePipelineStatus(accountPipelineStatus) &&
        !requiresPassword));
  const hasActiveLocalUpload = uploadItems.some((item) =>
    ACTIVE_UPLOAD_STATUSES.has(item.status)
  );
  const deletionDisabled =
    hasBackendProcessing || hasActiveLocalUpload || deleteLoading;
  const showAccountBody = isEmptyAccount || expanded;
  const resolvedBankIconUrl =
    bankIconUrl && failedBankIconUrl !== bankIconUrl ? bankIconUrl : null;
  // Only ever a gate on a document that was actually uploaded for a range —
  // never a clause on its own, or every range row would spin whenever the
  // account is busy.
  const accountPipelineActive =
    isActivePipelineStatus(accountPipelineStatus) ||
    ["PROCESSING", "VERIFYING"].includes(
      String(account.lastAnalysis?.status || "").toUpperCase()
    );
  const isRangeProcessing = useCallback(
    (rangeUpload: PulseRangeUploadInput) =>
      uploadItems.some(
        (item) =>
          item.rangeProcessing &&
          isSameRangeUpload(item.rangeUpload, rangeUpload)
      ) ||
      // Scoped to this account's own documents: if identification moves the
      // statement to another account (or it fails), the gap can never be filled
      // by it, so the row must stop showing progress.
      account.documents.some(
        (doc) =>
          doc.uploadedForRange?.accountId === account.id &&
          isSameRangeUpload(doc.uploadedForRange, rangeUpload) &&
          isRangeUploadPending(doc, accountPipelineActive)
      ),
    [account.documents, account.id, accountPipelineActive, uploadItems]
  );

  useEffect(() => {
    if (!menuOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  const openDeleteDialog = () => {
    if (deletionDisabled) return;
    setMenuOpen(false);
    setDeleteError("");
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteError("");
    setDeleteDialogOpen(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteLoading || deletionDisabled) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await deleteAccount(account.id);
      setDeleteDialogOpen(false);
    } catch (error) {
      setDeleteError(
        getApiErrorMessage(error, "This account could not be deleted. Please try again.")
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openUploadPicker = (rangeUpload?: PulseRangeUploadInput) => {
    if (isEmptyAccount && !expanded) toggleExpanded();
    uploadQueueRef.current?.openFilePicker(rangeUpload);
  };

  return (
    <div
      className={`${styles.accountCard} ${
        isEmptyAccount ? styles.emptyAccountCard : ""
      }`}
    >
      <div
        className={`${styles.accountHeader} ${
          isEmptyAccount ? styles.emptyAccountHeader : ""
        } ${!isEmptyAccount ? styles.clickableAccountHeader : ""}`}
        onClick={!isEmptyAccount ? toggleExpanded : undefined}
      >
        <div className={styles.emptyBankIcon} aria-hidden="true">
          {resolvedBankIconUrl ? (
            <img
              className={styles.bankLogo}
              src={resolvedBankIconUrl}
              alt=""
              onError={() => setFailedBankIconUrl(resolvedBankIconUrl)}
            />
          ) : (
            <HiOutlineLibrary />
          )}
        </div>
        <div className={styles.accountIdentity}>
          <strong className={styles.accountTitle}>
            <span className={styles.bankName} title={bankName}>
              {truncateBankName(bankName)}
            </span>
            <span className={styles.maskedAccountNumber}>
              {maskAccountNumber(account.accountNumber)}
            </span>
          </strong>
          {account.accountName ? (
            <span className={styles.accountName}>{account.accountName}</span>
          ) : null}
        </div>
        <div className={styles.accountActions}>
          {isEmptyAccount ? (
            <span
              title={
                deletionDisabled
                  ? "Account work is processing"
                  : "Delete account"
              }
            >
              <MeritoButton
                type="destructive-ghost"
                size="md2"
                onlyIcon
                hover={false}
                disabled={deletionDisabled}
                className={styles.emptyAccountDelete}
                icon={<img src={deleteAccountIcon} alt="" />}
                onClick={openDeleteDialog}
              />
            </span>
          ) : (
            <>
              <div
                className={styles.accountMenuRoot}
                ref={accountMenuRef}
                onClick={(event) => event.stopPropagation()}
              >
                <MeritoButton
                  type="tertiary"
                  size="md"
                  onlyIcon
                  hover={false}
                  className={styles.accountMoreButton}
                  icon={<HiDotsVertical />}
                  onClick={() => setMenuOpen((current) => !current)}
                />
                {menuOpen ? (
                  <div className={styles.accountMenu}>
                    <MeritoButton
                      type="destructive-ghost"
                      size="md2"
                      disabled={deletionDisabled}
                      className={styles.accountDeleteMenuButton}
                      icon={<img src={deleteAccountIcon} alt="" />}
                      btnText="Delete account"
                      onClick={openDeleteDialog}
                    />
                  </div>
                ) : null}
              </div>
              <MeritoButton
                type="tertiary"
                size="md"
                onlyIcon
                hover={false}
                className={styles.accountChevronButton}
                icon={expanded ? <HiChevronDown /> : <HiChevronRight />}
                ariaLabel={expanded ? "Collapse account" : "Expand account"}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleExpanded();
                }}
              />
            </>
          )}
        </div>
      </div>

      {!isEmptyAccount && !expanded ? (
        <AccountStatus account={account} />
      ) : null}

      {showAccountBody ? (
        <div
          className={`${styles.accountBody} ${
            isEmptyAccount ? styles.emptyAccountBody : ""
          }`}
        >
          <div className={styles.accountUploadAction}>
            <MeritoButton
              type="inverse"
              size="md2"
              btnText="Upload Statements"
              onClick={() => openUploadPicker()}
            />
          </div>
          {!isEmptyAccount ? (
            <MissingPeriodPanel
              account={account}
              onUpload={openUploadPicker}
              isRangeProcessing={isRangeProcessing}
            />
          ) : null}
          <FileUploadQueue
            key={`account-upload-${account.id}`}
            ref={uploadQueueRef}
            scope={`account-${account.id}`}
            accountId={account.id}
            accountNumber={account.accountNumber}
            autoSubmit
            hideTrigger
            onItemsChange={setUploadItems}
            onReady={handleReady}
            accountListChildren={
              visibleDocuments.length ? (
                <DocumentList
                  embedded
                  documents={visibleDocuments}
                  onRemove={(doc) => removeDocument(doc.id, true)}
                  onSubmitPassword={async (doc, password, signal) => {
                    await submitDocumentPassword(doc, password, {
                      submitAfterSave: true,
                      signal,
                    });
                  }}
                />
              ) : null
            }
          />
        </div>
      ) : null}
      <ConfirmDialog
        visible={deleteDialogOpen}
        toggle={closeDeleteDialog}
        showButton={false}
        showCloseBtn={false}
        backdrop="static"
        modalStyles={DIALOG_MODAL_STYLES}
        modalClassName={styles.deleteAccountDialogModal}
        contentClassName={styles.deleteAccountDialogContent}
        bodyClassName={styles.deleteAccountDialogBody}
        size="md"
      >
        <div className={styles.deleteAccountDialog}>
          <div className={styles.deleteAccountMessage}>
            <BsFillExclamationCircleFill aria-hidden="true" />
            <h2>Delete this account?</h2>
            <p>The account and its uploaded statements will be deleted.</p>
            {deleteError ? (
              <div className={styles.deleteAccountError}>{deleteError}</div>
            ) : null}
          </div>
          <div className={styles.deleteAccountActions}>
            <MeritoButton
              type="secondary-2"
              size="lg"
              isRounded
              btnText="Cancel"
              disabled={deleteLoading}
              onClick={closeDeleteDialog}
            />
            <MeritoButton
              type="primary-1"
              size="lg"
              isRounded
              btnText="Delete Account"
              loading={deleteLoading}
              disabled={deletionDisabled}
              onClick={handleDeleteAccount}
            />
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
};
